import React, { useEffect, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { Delete } from '@mui/icons-material';
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt';
import { formatCurrency, safelyParseFloat, toCamelCase } from '@/utils/constants';
import DataGridNumericFormat from '@/components/table/DataGridComponents/DataGridNumericFormat';
import DataGridExcessSelect from '@/components/table/DataGridComponents/DataGridExcessSelect';
import useCellModes from '@/hooks/useCellModes';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetClassOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';

export const ResultsTable = ({
	rows,
	updateRows,
	removeRow,
	classOfRisk,
	setRowSelectionModel,
	rowSelectionModel,
	disabled,
	isDomestic,
}) => {
	const theme = useTheme();
	const { cellModesModel, handleCellClick, handleCellModesModelChange } = useCellModes();

	const { username: email } = useSelector((state) => state.msalAccount.account);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classOfRiskDoc, ...classesOfRiskQuery } = useGetClassOfRiskQuery(
		{ countryCode, classOfRisk: toCamelCase(classOfRisk), brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized || !classOfRisk },
	);

	const [hoveredId, setHoveredId] = useState();

	const shownColumns = classOfRiskDoc?.columns ?? {};

	const columnVisibilityModel = Object.keys(shownColumns)
		.filter((r) => !shownColumns[r])
		.reduce((acc, r) => {
			acc[r] = shownColumns[r];
			return acc;
		}, {});

	const columns = [
		{
			...GRID_CHECKBOX_SELECTION_COL_DEF,
			renderHeader: () => <></>,
			renderCell: (params) =>
				params.row.insurer === '' ? (
					<></>
				) : (
					GRID_CHECKBOX_SELECTION_COL_DEF.renderCell(params)
				),
		},
		{
			field: 'insurer',
			headerName: 'Insurer',
			flex: 1,
			minWidth: 200,
			editable: !disabled,
			sortable: false,
			display: 'flex',
			renderCell: (params) =>
				params.row.insurer === '' ? (
					<Typography sx={{ color: theme.palette.text.secondary }}>Enter name of insurer</Typography>
				) : (
					<Box
						sx={{ position: 'relative' }}
						width='100%'
						height='100%'
						onMouseEnter={() => setHoveredId(params.row.insurer)}
						onMouseLeave={() => setHoveredId(null)}
					>
						<Stack
							direction='row'
							justifyContent='space-between'
							width='100%'
							height='100%'
							alignItems='center'
						>
							<Typography
								sx={{
									...(params.row.declined && { textDecoration: 'line-through' }),
								}}
							>
								{params.row.insurer}
							</Typography>
							{params.row.declined && (
								<Tooltip title='Re-enter premium to remove declined status'>
									<Typography sx={{ fontSize: '12px', color: 'indianred' }}>
										{'(declined)'}
									</Typography>
								</Tooltip>
							)}
						</Stack>
						{hoveredId === params.row.insurer && !params.row.declined && (
							<Tooltip title='Decline quote'>
								<div>
									<IconButton
										sx={{
											fontSize: '10px',
											padding: 0,
											position: 'absolute',
											top: '50%',
											right: 0,
											transform: 'translate(50%, -50%)',
										}}
										onClick={() =>
											updateRows(
												{ ...params.row, declined: !params.row.declined },
												params.row,
											)
										}
									>
										<ThumbDownOffAltIcon fontSize='small' />
									</IconButton>
								</div>
							</Tooltip>
						)}
					</Box>
				),
		},
		{
			field: 'sumInsured',
			headerName: 'Sum Insured ($)',
			flex: 0.3,
			minWidth: 150,
			editable: false,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				const value = safelyParseFloat(params.row.sumInsured ?? 0);
				if (value > 0) {
					return formatCurrency({ num: value, showDecimal: false });
				}
			},
		},
		{
			field: 'excess',
			headerName: 'Excess ($)',
			flex: 0.3,
			minWidth: 150,
			editable: !disabled,
			sortable: false,
			display: 'flex',
			align: 'right',
			headerAlign: 'right',
			singleClickCell: true,
			renderCell: (params) => {
				const value = parseInt(params.row.excess ?? 0);
				if (params.row.insurer === '' || (params.row.excess ?? '').length === 0) {
					return (
						<Typography sx={{ color: theme.palette.text.secondary }}>
							{params.row.insurer === '' ? '' : 'Enter excess'}
						</Typography>
					);
				}
				return /[^0-9.]/.test(params.row.excess ?? '')
					? params.row.excess
					: formatCurrency({ num: value, showDecimal: false });
				// return formatCurrency(value, false);
			},
			renderEditCell: (params) => <DataGridExcessSelect {...params} />,
		},
		{
			field: 'companyPremium',
			headerName: `${isDomestic ? 'Base' : 'Company'} Premium (excl.GST) ($)`,
			flex: 0.3,
			minWidth: 150,
			editable: false,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				const premium = safelyParseFloat(params.row.companyPremium ?? 0);
				if (params.row.insurer === '') {
					return <></>;
				}
				return formatCurrency({ num: premium, showDecimal: true });
			},
		},
		{
			field: 'naturalDisaster',
			headerName: 'Natural Disaster Premium ($)',
			flex: 0.3,
			minWidth: 150,
			editable: !disabled,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				let value = parseFloat(params.row.naturalDisaster ?? 0);
				value = isNaN(value) ? 0 : value;
				if (params.row.insurer === '') {
					return <></>;
				}
				return formatCurrency({ num: value, showDecimal: true });
			},
			renderEditCell: (params) => (
				<DataGridNumericFormat {...params} isCurrency={true} decimalScale={2} />
			),
		},
		{
			field: 'nhi',
			headerName: 'Natural Hazards Insurance Levy ($)',
			flex: 0.3,
			minWidth: 150,
			editable: !disabled,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				let value = parseFloat(params.row.nhi ?? 0);
				value = isNaN(value) ? 0 : value;
				if (params.row.insurer === '') {
					return <></>;
				}
				return formatCurrency({ num: value, showDecimal: true });
			},
			renderEditCell: (params) => (
				<DataGridNumericFormat {...params} isCurrency={true} decimalScale={2} />
			),
		},
		{
			field: 'fsl',
			headerName: 'Fire Service Levy (FSL) ($)',
			flex: 0.3,
			minWidth: 150,
			editable: !disabled,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				const value = safelyParseFloat(params.row.fsl ?? 0);
				// value = isNaN(value) ? 0 : value;
				if (params.row.insurer === '') {
					return <></>;
				}
				return formatCurrency({ num: value, showDecimal: true });
			},
			renderEditCell: (params) => (
				<DataGridNumericFormat {...params} isCurrency={true} decimalScale={2} />
			),
		},
		{
			field: 'gst',
			headerName: 'GST ($)',
			flex: 0.3,
			minWidth: 150,
			editable: false,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				const value = safelyParseFloat(params.row.gst ?? 0);
				// let value = parseFloat(params.row.premium ?? 0);
				// value = isNaN(value) ? 0 : value;
				// const gst = value * 0.15;
				if (params.row.insurer === '') {
					return <></>;
				}
				return formatCurrency({ num: value, showDecimal: true });
			},
		},
		{
			field: 'premium',
			headerName: 'Premium (incl.GST) ($)',
			flex: 0.3,
			minWidth: 150,
			editable: !disabled,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			display: 'flex',
			renderCell: (params) => {
				const value = safelyParseFloat(params.row.premium ?? 0);
				// value = isNaN(value) ? 0 : value;
				if (params.row.insurer === '' || value === 0) {
					return (
						<Typography sx={{ color: theme.palette.text.secondary }}>
							{params.row.insurer === '' ? '' : 'Enter premium'}
						</Typography>
					);
				}
				return formatCurrency({ num: value, showDecimal: true });
			},
			renderEditCell: (params) => (
				<DataGridNumericFormat {...params} isCurrency={true} decimalScale={2} />
			),
			cellClassName: 'pinned-column',
			headerClassName: 'pinned-column',
		},
		{
			// ...GRID_CHECKBOX_SELECTION_COL_DEF,
			field: 'delete',
			headerName: 'Delete',
			width: 48,
			sortable: false,
			resizable: false,
			display: 'flex',
			renderHeader: (params) => <></>,
			renderCell: (params) =>
				params.row.insurer === '' || rows.length <= 1 ? (
					<></>
				) : (
					<IconButton
						sx={{ fontSize: '10px', padding: 0 }}
						color='rgba(0,0,0,0)'
						onClick={() => removeRow(params.row.id)}
					>
						<Delete fontSize='small' />
					</IconButton>
				),
			cellClassName: 'pinned-column',
			headerClassName: 'pinned-column',
		},
	];

	return (
		<DataGrid
			disableVirtualization
			width='100%'
			processRowUpdate={updateRows}
			rows={rows}
			columns={columns}
			isCellEditable={(params) => {
				if (disabled) {
					return false;
				}
				if (params.field === 'insurer') {
					return true;
				}
				return (params.row.insurer ?? '').length > 0;
			}}
			isRowSelectable={(params) => {
				const sumInsured = parseInt((params.row?.sumInsured ?? '').toString());
				const excess = parseInt((params.row?.excess ?? '').toString());
				const premium = parseInt((params.row?.premium ?? '').toString());
				return (
					!isNaN(sumInsured) &&
					sumInsured > 0 &&
					(params.row?.excess ?? '').toString().trim().length > 0 &&
					// !isNaN(excess) &&
					// excess > 0 &&
					!isNaN(premium) &&
					premium > 0 &&
					(params.row?.insurer ?? '').toString().length > 0
				);
			}}
			getRowId={(row) => `${row.insurer}`}
			columnVisibilityModel={columnVisibilityModel}
			checkboxSelection
			onRowSelectionModelChange={setRowSelectionModel}
			rowSelectionModel={rowSelectionModel}
			cellModesModel={cellModesModel}
			onCellModesModelChange={handleCellModesModelChange}
			onCellClick={handleCellClick}
			disableRowSelectionOnClick
			disableSelectionOnClick
			disableColumnFilter
			disableColumnSelector
			disableColumnMenu
			hideFooter
		/>
	);
};
